/**
 * 🔒 Decision 7620 (T3124, the owner's I1a, I2a and I3a of 2026-10-25): what
 * the build claims about stored data is provable from a screen a super admin
 * can open. Three screens read it, and each reads counts and keys, never a
 * file's bytes or a record's values: the Storage block on a file's details,
 * the stored-objects card on Storage and Backups, and Inspect from a record's
 * banner. The API answers these shapes and the web draws them, so the two
 * cannot disagree about a field.
 */

/** 🔒 The one refusal all three routes answer to anybody who is not a super admin. */
export const PROVE_IT_SUPER_ADMIN_REFUSAL = 'What the store holds and what points at a record are a super admin’s to read.';

/** The Storage block's read: the stored object behind one file (I1a). */
export function fileStoragePath(fileId: string): string {
  return `/files/${fileId}/storage`;
}

/** The stored-objects card's read: the quota's sum and the last read of the store (I2a). */
export const STORE_FACTS_PATH = '/settings/storage/objects';

/** Read the Store Now: the listing of the store under this server's prefix, made when it is pressed (I2a). */
export const STORE_FACTS_READ_PATH = '/settings/storage/objects/read';

/** Inspect's read: one record's id, its version, what points at it and its audit count (I3a). */
export function inspectPath(recordType: string, recordId: string): string {
  return `/inspect/${encodeURIComponent(recordType)}/${encodeURIComponent(recordId)}`;
}

/** One Mirror's copy of a stored object, from the backup ledger: the hash it copied and when. */
export interface StoredObjectMirrorCopy {
  targetName: string;
  contentHash: string;
  copiedAt: string;
  /** True when the copied hash is the version's own, so the copy is of these bytes. */
  sameHash: boolean;
}

/** 🔒 I1a: the Storage block on a file's details. */
export interface FileStorageFacts {
  versionNumber: number;
  /** The stored object's key, with its environment prefix where it carries one. */
  key: string;
  /** The prefix the key begins with, or null for a key written before the prefix (decision 7540). */
  keyPrefix: string | null;
  /** The version's own size, the figure the quota counts. */
  sizeBytes: number;
  /** The object's size as the store answers it, or null where the store holds nothing at the key. */
  storedBytes: number | null;
  /** True when the store holds no object at the key. */
  missing: boolean;
  /** True when the object is encrypted under its own data key (decision 7420). */
  sealed: boolean;
  /** The content hash the version records. */
  contentHash: string;
  /** Every file whose live version names this object: the ones the reader may open by name, the rest as a count. */
  sharedBy: { total: number; files: { id: string; name: string }[]; hidden: number };
  /** Each Mirror holding a copy of the object. */
  mirrors: StoredObjectMirrorCopy[];
}

/** 🔒 I2a: one listing of the store, made on demand. */
export interface StoreRead {
  readAt: string;
  /** Every object this server owns on the store: its prefix, and on Run or a developer's machine the keys from before it. */
  objects: number;
  bytes: number;
  /** The objects a version row names, and their bytes. */
  fileObjects: number;
  fileBytes: number;
  /** Everything else: thumbnails, sealed copies left behind, dumps and objects no row names. */
  otherObjects: number;
  otherBytes: number;
  /** The keys this server owns that a live version names and the store does not hold. */
  missingObjects: number;
  /** The keys a live version names that another environment owns, read where they are and never listed here (decision 7540). */
  foreignObjects: number;
  /** What the quota counts for the listed file objects less those objects' bytes: the bytes counted more than once because versions share an object. */
  sharedBytes: number;
}

/** 🔒 I2a: the stored-objects card on Storage and Backups. */
export interface StoreFactsView {
  /** The prefix this server writes under. */
  prefix: string;
  /** What the quota counts: every live version, each at its own size. */
  quota: { versions: number; bytes: number };
  /** The objects those versions name, counted once each, from the database alone. */
  distinct: { objects: number; bytes: number };
  /** The last listing of the store this server made since it started, or null before the first. */
  read: StoreRead | null;
  /**
   * 🔒 The store's refusal at the last attempt, when that attempt could not list it (no bucket, no connection, a
   * refused credential, a timeout), in a sentence from the string table, with when it was tried; null once a listing
   * succeeds. The figures in `read` stay as the last good listing left them.
   */
  failure: { attemptedAt: string; reason: string } | null;
}

/** One table and column that names the record, with how many rows do. */
export interface InspectPointer {
  table: string;
  column: string;
  count: number;
}

/** One stored object a record's files name. */
export interface InspectObject {
  key: string;
  sizeBytes: number;
  sealed: boolean;
  /** How many live versions name the object, across every file. */
  versions: number;
}

/** 🔒 I3a: Inspect, read-only, counts and keys and never values. */
export interface InspectAnswer {
  recordType: string;
  recordId: string;
  table: string;
  /** The record's version counter (piece 7), or null where the record carries none. */
  version: number | null;
  /** Where the tables read came from: the merge map's list, or the database's own references to the table. */
  source: 'mergeMap' | 'references';
  /** How many table and column pairs were read, the ones with no row included. */
  columnsRead: number;
  /** The pairs with at least one row, by table. */
  pointers: InspectPointer[];
  auditRows: number;
  /** The record's files and their stored objects, or null where it has none. */
  files: { files: number; versions: number; objects: number; bytes: number; sealed: number } | null;
  objects: InspectObject[];
  /** The objects past the list's cap, counted. */
  moreObjects: number;
}

/** 🔒 Inspect lists this many stored objects and counts the rest. */
export const INSPECT_OBJECT_CAP = 50;

/** 🔒 Read the Store Now gives up on a listing that has not finished in this long, and answers the timeout as the store's refusal. */
export const STORE_READ_TIMEOUT_MS = 120_000;
